import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthTokens } from '@/types';
import type { CloudBaseUser } from '@/services/cloudbase/types';
import { STORAGE_KEYS } from '@/config/constants';
import { setTokens as saveTokensToStorage, clearTokens } from '@/services/api';
import { queryClient } from '@/config/queryClient';

interface AuthState {
  // State
  user: CloudBaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  rememberMe: boolean;

  // Actions
  setUser: (user: CloudBaseUser | null) => void;
  setTokens: (tokens: AuthTokens) => void;
  login: (user: CloudBaseUser, tokens: AuthTokens, remember?: boolean) => void;
  logout: () => void;
  updateUser: (updates: Partial<CloudBaseUser>) => void;
  setLoading: (loading: boolean) => void;
  setRememberMe: (remember: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      rememberMe: false,

      // Set user
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      // Set authentication tokens
      setTokens: (tokens) => {
        saveTokensToStorage(tokens);
      },

      // Login
      login: (user, tokens, remember = false) => {
        // Save tokens to appropriate storage
        queryClient.clear();
        clearTokens();
        if (remember) {
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
        } else {
          sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
          sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
        }

        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          rememberMe: remember,
        });
      },

      // Logout
      logout: () => {
        queryClient.clear();
        clearTokens();
        // Clear from both storages
        sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          rememberMe: false,
        });
      },

      // Update user information
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      // Set loading state
      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),

      // Set remember me
      setRememberMe: (remember) =>
        set({
          rememberMe: remember,
        }),
    }),
    {
      name: STORAGE_KEYS.USER,
      partialize: (state) => ({
        user: state.rememberMe ? state.user : null,
        isAuthenticated: state.rememberMe ? state.isAuthenticated : false,
        rememberMe: state.rememberMe,
      }),
    }
  )
);
